// src/routes/HospitalRoutes.js
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import DashboardLayout from '../components/layout/DashboardLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import RoleBasedRoute from '../components/auth/RoleBasedRoute';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Hospital Pages - Using lazy loading for better performance
const HospitalDashboard = React.lazy(() => import('../pages/hospital/HospitalDashboard'));
const ManageServices = React.lazy(() => import('../pages/hospital/ManageServices'));
const ManageDoctors = React.lazy(() => import('../pages/hospital/ManageDoctors'));
const ManageAmbulances = React.lazy(() => import('../pages/hospital/ManageAmbulances'));
const Appointments = React.lazy(() => import('../pages/hospital/Appointments'));
const PatientAdmissions = React.lazy(() => import('../pages/hospital/PatientAdmissions'));
const Inventory = React.lazy(() => import('../pages/hospital/Inventory'));
const Reports = React.lazy(() => import('../pages/hospital/Reports'));
const HospitalProfile = React.lazy(() => import('../pages/hospital/HospitalProfile'));
const SubscriptionManager = React.lazy(() => import('../pages/hospital/SubscriptionManager'));

// Loading component for lazy loaded routes
const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="large" />
  </div>
);

const HospitalRoutes = () => {
  return (
    <Routes>
      {/* Protected Hospital Portal Routes - MUST COME FIRST */}
      <Route 
        path="/*" 
        element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['hospital']}>
              <Routes>
                <Route path="/" element={<DashboardLayout />}>
                  {/* Dashboard - Default route */}
                  <Route 
                    index 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Hospital Dashboard - MediGo</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <HospitalDashboard />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Hospital Profile - SPECIFIC ROUTE */}
                  <Route 
                    path="profile" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Hospital Profile - MediGo</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <HospitalProfile />
                      </React.Suspense>
                    } 
                  />

                  {/* Subscription Management */}
                  <Route 
                    path="subscription" 
                    element={ 
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Subscription Management - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <SubscriptionManager />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Services Management */}
                  <Route 
                    path="services" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Manage Services - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <ManageServices />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Doctors Management */}
                  <Route 
                    path="doctors" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Manage Doctors - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <ManageDoctors />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Ambulances Management */}
                  <Route 
                    path="ambulances" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Manage Ambulances - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <ManageAmbulances />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Appointments */}
                  <Route 
                    path="appointments" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Appointments - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <Appointments />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Patient Admissions */}
                  <Route 
                    path="admissions" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Patient Admissions - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <PatientAdmissions />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Inventory Management */}
                  <Route 
                    path="inventory" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Inventory Management - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <Inventory />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Reports & Analytics */}
                  <Route 
                    path="reports" 
                    element={
                      <React.Suspense fallback={<RouteLoader />}>
                        <Helmet>
                          <title>Reports & Analytics - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <Reports />
                      </React.Suspense>
                    } 
                  />
                  
                  {/* Catch all route for hospital portal section - 404 */}
                  <Route 
                    path="*" 
                    element={
                      <>
                        <Helmet>
                          <title>Page Not Found - MediGo Hospital Portal</title>
                          <meta name="robots" content="noindex, nofollow" />
                        </Helmet>
                        <div className="flex flex-col items-center justify-center min-h-[60vh]">
                          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Page Not Found</h2>
                          <p className="text-gray-500 mb-8">The page you're looking for doesn't exist in the hospital portal.</p>
                          <a 
                            href="/hospital" 
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Back to Dashboard
                          </a>
                        </div>
                      </>
                    } 
                  />
                </Route>
              </Routes>
            </RoleBasedRoute>
          </ProtectedRoute>
        }
      />

      
    </Routes>
  );
};

export default HospitalRoutes;